import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  onCheckedChange?: (checked: boolean) => void;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, checked, defaultChecked, disabled, onChange, onCheckedChange, ...props }, ref) => {
    const reactId = React.useId();
    const resolvedId = props.id ?? reactId;
    const isControlled = typeof checked === 'boolean';
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState<boolean>(() => !!defaultChecked);
    const visualChecked = isControlled ? !!checked : uncontrolledChecked;

    return (
      <label
        htmlFor={resolvedId}
        className={cn(
          'flex items-center gap-4 p-4 rounded-xl transition-all duration-300 border-2 cursor-pointer select-none',
          disabled && 'opacity-60 cursor-not-allowed',
          !disabled &&
            (visualChecked
              ? 'bg-primary/5 border-primary scale-[1.01]'
              : 'bg-white border-gray-200 hover:border-primary hover:bg-primary/5 hover:scale-[1.01]'),
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/40',
          className
        )}
      >
        <input
          ref={ref}
          id={resolvedId}
          type="checkbox"
          className="sr-only"
          {...(isControlled ? { checked: !!checked } : {})}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={(e) => {
            if (!isControlled) setUncontrolledChecked(e.target.checked);
            onChange?.(e);
            onCheckedChange?.(e.target.checked);
          }}
          {...props}
        />
        <span
          className={cn(
            'h-6 w-6 min-w-6 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
            visualChecked ? 'bg-primary border-primary text-white' : 'bg-white border-gray-300'
          )}
          aria-hidden="true"
        >
          {visualChecked && <Check className="h-4 w-4" />}
        </span>
        {(label || description) && (
          <span className="min-w-0">
            {label && <span className="block text-base font-medium leading-tight text-gray-700">{label}</span>}
            {description && <span className="mt-1 block text-sm text-muted-foreground leading-snug">{description}</span>}
          </span>
        )}
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
